import { UploadedFile } from '@/type';
import api from '../../API';

interface Upload {
  file: UploadedFile,
  location: string
}

async function createUpload(file: File): Promise<Upload> {
  return api
    .post('uploads', { name: file.name, size: file.size })
    .then(response => ({
      file: response.data,
      location: response.headers.location,
    }));
}

const partSize = 10000000;

interface OnProgress {
  (percent: number, partSize: number): void
}

async function uploadPart(
  location: string,
  partNumber: number,
  file: File,
  onProgress: OnProgress,
): Promise<void> {
  const start = (partNumber - 1) * partSize;
  const part = file.slice(start, start + partSize);

  const data = new FormData();
  data.append('partNumber', `${partNumber}`);
  data.append('file', new File([part], file.name));
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress(e: ProgressEvent) {
      onProgress(Math.round((e.loaded * 100) / e.total), part.size);
    },
  };
  return api
    .post(location, data, config)
    .then(() => undefined);
}

interface UploadOptions {
  onProgress?: (progress: number) => void
}

export async function upload(file: File, options: UploadOptions): Promise<UploadedFile> {
  const newUpload = await createUpload(file);
  const totalParts = Math.ceil(file.size / partSize);
  let partNumber = 1;
  for (; partNumber <= totalParts; partNumber += 1) {
    // eslint-disable-next-line no-await-in-loop,no-loop-func,max-len
    await uploadPart(newUpload.location, partNumber, file, (progress: number, chunkSize: number) => {
      if (!options.onProgress) return;
      let bytesUploaded = (partNumber - 1) * partSize;
      bytesUploaded += (progress / 100) * chunkSize;
      options.onProgress((bytesUploaded * 100 / file.size));
    });
  }
  return Promise.resolve(newUpload.file);
}
